// ─────────────────────────────────────────────────────────────────────────────
// SCORING V2 — Weighted Capture Probability Model
//
// Computes 5 component scores from existing opportunity fields.
// No external API calls — all inputs are available at display time.
// Tune WEIGHTS and thresholds below to adjust model behavior.
// ─────────────────────────────────────────────────────────────────────────────

// ── Weights (must sum to 1.0) ──────────────────────────────────────────────
const WEIGHTS = {
  capabilityFit:        0.30,  // Does this match what we can deliver?
  marketAttractiveness: 0.20,  // Is this a good market opportunity?
  competitivePosture:   0.20,  // Can we win against the field?
  executionFeasibility: 0.15,  // Can we respond given timeline + data?
  strategicValue:       0.15,  // Does this serve BD strategy?
};

// TODO(multi-tenant): these mission-area keywords are defense/IT-services biased.
// For non-defense or state-contract tenants they over-weight defense opportunities.
// Replace with a profile-driven keyword list pulled from the user's company profile.
const MISSION_KEYWORDS = [
  'c2', 'command and control', 'command & control', 'jadc2', 'abms',
  'cyber', 'cybersecurity', 'zero trust',
  'devsecops', 'devops', 'software factory',
  'cloud', 'cloud migration', 'cloud native',
  'artificial intelligence', 'machine learning', 'ai/ml',
  'autonomous', 'autonomy',
  'data analytics', 'data engineering', 'data platform',
  'network operations', 'netops',
  'mission systems', 'c4isr', 'isr',
  'human machine interface', 'hmi', 'operator interface',
  'software engineering', 'software development',
  'system integration', 'integration services',
];

const LARGE_PRIMES = [
  'raytheon', 'l3harris', 'northrop grumman', 'lockheed martin',
  'general dynamics', 'booz allen', 'leidos', 'saic', 'caci',
  'perspecta', 'maximus', 'accenture federal', 'deloitte federal',
];

// Parse display-formatted value strings like "$5.0M", "$250K", "$1.5B" back to numeric
function parseDisplayValue(str) {
  if (!str || typeof str !== 'string') return 0;
  const m = str.match(/\$([\d.]+)(B|M|K)?/i);
  if (!m) return 0;
  const num = parseFloat(m[1]);
  if (isNaN(num)) return 0;
  const suffix = (m[2] || '').toUpperCase();
  if (suffix === 'B') return num * 1e9;
  if (suffix === 'M') return num * 1e6;
  if (suffix === 'K') return num * 1e3;
  return num;
}

function countKeywords(text, keywords) {
  const lower = (text || '').toLowerCase();
  return keywords.filter(k => lower.includes(k)).length;
}

function daysUntil(isoDate) {
  if (!isoDate) return null;
  return Math.ceil((new Date(isoDate).getTime() - Date.now()) / 86400000);
}

// ── Component 1: Capability Fit ───────────────────────────────────────────
// Primary: Claude fit reasons. Secondary: AI score + keyword density.
function computeCapabilityFit(opp) {
  const signals = [];
  let score = 0;

  const fitCount = (opp.fitReasons || []).length;
  if      (fitCount >= 4) { score += 55; signals.push(`${fitCount} capability alignment signals from AI analysis`); }
  else if (fitCount === 3) { score += 44; signals.push('3 capability alignment signals'); }
  else if (fitCount === 2) { score += 32; signals.push('2 capability alignment signals'); }
  else if (fitCount === 1) { score += 20; signals.push('1 capability alignment signal'); }

  // AI score corroborates fit
  const aiScore = opp.score || 0;
  if      (aiScore >= 80) { score += 25; if (fitCount === 0) signals.push(`AI fit score: ${aiScore}/100`); }
  else if (aiScore >= 65) { score += 18; if (fitCount === 0) signals.push(`AI fit score: ${aiScore}/100`); }
  else if (aiScore >= 50) { score +=  9; }

  // Mission-area keyword density
  const text = `${opp.title || ''} ${opp.description || ''} ${(opp.fitReasons || []).join(' ')}`;
  const kwCount = countKeywords(text, MISSION_KEYWORDS);
  score += Math.min(kwCount * 4, 20);
  if (kwCount >= 4 && fitCount === 0) signals.push(`${kwCount} mission-area keywords in solicitation`);
  else if (kwCount >= 2 && signals.length === 0) signals.push(`${kwCount} relevant keywords matched`);

  return { score: Math.min(Math.round(score), 100), signals, label: 'Capability Fit', weight: WEIGHTS.capabilityFit };
}

// ── Component 2: Market Attractiveness ────────────────────────────────────
// Contract value + intel depth + recency + set-aside + graph density
function computeMarketAttractiveness(opp) {
  const signals = [];
  let score = 15;
  const gs = opp.graphSignals || {};

  // Use raw numeric value if available, otherwise try to parse display string
  const val = opp.estimatedValueNumeric || parseDisplayValue(opp.estimatedValue);
  if      (val >= 50000000) { score += 25; signals.push(`High-value contract ($${(val / 1e6).toFixed(0)}M+)`); }
  else if (val >= 10000000) { score += 18; signals.push(`Substantial contract ($${(val / 1e6).toFixed(0)}M)`); }
  else if (val >= 1000000)  { score += 10; signals.push(`Mid-size contract ($${(val / 1e6).toFixed(1)}M)`); }
  else if (val > 0)         { score +=  5; }

  if ((opp.competitiveIntel || '').length > 200) { score += 15; signals.push('Competitive intel on file'); }
  if (opp.marketResearch?.intelligence)          { score += 12; signals.push('Market research available'); }

  const daysSincePost = opp.postedDate
    ? Math.floor((Date.now() - new Date(opp.postedDate).getTime()) / 86400000)
    : 999;
  if      (daysSincePost <= 7)  { score += 15; signals.push('Posted this week — early engagement window'); }
  else if (daysSincePost <= 21) { score += 10; signals.push('Recently posted'); }
  else if (daysSincePost <= 60) { score +=  5; }

  const sa = (opp.setAside || '').trim();
  if (sa && !['N/A', 'None', 'nan', ''].includes(sa)) {
    score += 8;
    signals.push(`Set-aside: ${sa}`);
  }

  // Graph: agency-mission density indicates active market
  if (gs.agencyMissionDensity >= 5) {
    score += 8; signals.push(`High agency demand in ${(gs.linkedMissionAreas || []).slice(0, 2).join(', ') || 'mission area'}`);
  } else if (gs.agencyMissionDensity >= 3) {
    score += 4;
  }

  return { score: Math.min(Math.round(score), 100), signals, label: 'Market Attractiveness', weight: WEIGHTS.marketAttractiveness };
}

// ── Component 3: Competitive Posture ─────────────────────────────────────
// Higher = better position for us (less dominated field)
// Baseline reduced from 45→35: no intel should not mean strong posture
// Graph signals: contractor ecosystem size, connected contractor count
function computeCompetitivePosture(opp) {
  const signals = [];
  let score = 35;
  const gs = opp.graphSignals || {};

  const intel = (opp.competitiveIntel || '').toLowerCase();
  const sa = (opp.setAside || '').toLowerCase();

  if (/small business|sdvosb|wosb|8\(a\)|hubzone/.test(sa)) {
    score += 22; signals.push('Small-business set-aside limits competition');
  } else if (sa && !['n/a', 'none', 'nan', ''].includes(sa)) {
    score += 10;
  }

  if (/incumbent|sole source|follow-on|bridge contract|contract extension/.test(intel)) {
    score -= 18; signals.push('Incumbent or sole-source signals in intel');
  }

  const detectedPrimes = LARGE_PRIMES.filter(p => intel.includes(p));
  if      (detectedPrimes.length >= 3) { score -= 20; signals.push(`${detectedPrimes.length} large primes in competitive field`); }
  else if (detectedPrimes.length >= 1) { score -=  8; signals.push(`${detectedPrimes[0].replace(/\b\w/g, c => c.toUpperCase())} detected in competitive landscape`); }

  if (/new contract|open competition|no award history|emerging|first time/.test(intel)) {
    score += 12; signals.push('Open competition — no dominant incumbent');
  }

  // Graph: large contractor ecosystem suggests crowded field
  if (gs.contractorEcosystemSize >= 8) {
    score -= 10; signals.push(`Crowded ecosystem — ${gs.contractorEcosystemSize} contractors active at this agency`);
  } else if (gs.contractorEcosystemSize >= 4) {
    score -= 4;
  } else if (gs.contractorEcosystemSize > 0 && gs.contractorEcosystemSize <= 2) {
    score += 6; signals.push('Narrow contractor field at this agency');
  }

  return { score: Math.max(0, Math.min(Math.round(score), 100)), signals, label: 'Competitive Posture', weight: WEIGHTS.competitivePosture };
}

// ── Component 4: Execution Feasibility ──────────────────────────────────
// Deadline runway + extraction completeness + data availability
function computeExecutionFeasibility(opp) {
  const signals = [];
  let score = 20;

  const daysLeft = daysUntil(opp.responseDeadline);
  if      (daysLeft === null) { score += 0; signals.push('No response deadline on record — data gap'); }
  else if (daysLeft >= 60)    { score += 30; signals.push(`${daysLeft} days to respond — ample runway`); }
  else if (daysLeft >= 30)    { score += 22; signals.push(`${daysLeft} days to respond`); }
  else if (daysLeft >= 14)    { score += 12; signals.push(`${daysLeft} days remaining — begin soon`); }
  else if (daysLeft >= 7)     { score +=  3; signals.push(`${daysLeft} days left — urgent`); }
  else if (daysLeft >= 0)     { score -= 10; signals.push(`${daysLeft} days left — critical`); }
  else                        { score -= 25; signals.push('Response deadline has passed'); }

  if (opp.aiScrubStatus === 'complete') { score += 15; signals.push('AI extraction complete'); }
  if (opp.attachmentStatus === 'complete') { score += 15; }
  else if (opp.attachmentStatus === 'partial') { score +=  7; }
  if ((opp.attachmentText || '').length > 500) { score += 10; signals.push('Solicitation document available'); }
  if ((opp.description || '').length > 300)    { score +=  5; }

  return { score: Math.max(0, Math.min(Math.round(score), 100)), signals, label: 'Execution Feasibility', weight: WEIGHTS.executionFeasibility };
}

// ── Component 5: Strategic Value ─────────────────────────────────────────
// Pursuit status + BD priority + capture depth + agency alignment
function computeStrategicValue(opp) {
  const signals = [];
  let score = 15;

  const bds = (opp.bdStatus || 'new').toLowerCase();
  if (['pursuing', 'partnering', 'submitted'].includes(bds) || opp.recommendation === 'PURSUE') {
    score += 30; signals.push('Flagged for active pursuit');
  } else if (bds === 'won') {
    score += 35; signals.push('Won opportunity');
  } else if (bds === 'reviewing' || opp.recommendation === 'MONITOR') {
    score += 12;
  }

  if      (opp.priority === 'high')   { score += 18; signals.push('High BD priority'); }
  else if (opp.priority === 'medium') { score +=  8; }

  const openTasks = (opp.captureTasks || []).filter(t => !t.done).length;
  if      (openTasks >= 3) { score += 18; signals.push(`${openTasks} active capture tasks`); }
  else if (openTasks >  0) { score +=  8; signals.push(`${openTasks} capture task${openTasks > 1 ? 's' : ''} in progress`); }

  if ((opp.notes || '').length > 100) { score += 6; signals.push('Team notes on record'); }

  // User feedback labels boost/penalize when present
  if (opp.userFitLabel === 'strong-fit')   { score += 15; signals.push('User labeled Strong Fit'); }
  else if (opp.userFitLabel === 'good-fit')     { score +=  8; }
  else if (opp.userFitLabel === 'weak-fit')     { score -=  8; }
  else if (opp.userFitLabel === 'not-relevant') { score -= 20; signals.push('User labeled Not Relevant'); }

  const ag = (opp.agency || '').toLowerCase();
  if (/\b(army|navy|air force|marines|marine corps|space force|socom|darpa|disa|nga)\b/.test(ag)) {
    score += 14; signals.push('Core defense agency');
  } else if (/\b(dod|department of defense|cyber command|nsa|dia)\b/.test(ag)) {
    score += 10; signals.push('Defense/intel agency');
  } else if (/\b(dhs|homeland|tsa|cbp|fema)\b/.test(ag)) {
    score +=  6;
  }

  // Graph: program linkage strengthens strategic value
  const gs = opp.graphSignals || {};
  if (gs.linkedProgramCount >= 1 && gs.programStrength >= 0.7) {
    score += 12; signals.push(`Linked to ${(gs.linkedProgramNames || []).slice(0, 2).join(', ') || 'recognized program'}`);
  } else if (gs.linkedProgramCount >= 1) {
    score += 6;
  }

  // Graph: lifecycle thread indicates sustained effort
  if (gs.lifecycleThreadCount >= 2) {
    score += 8; signals.push('Part of active lifecycle thread with prior notices');
  } else if (gs.relatedNoticeCount >= 2) {
    score += 4;
  }

  return { score: Math.min(Math.round(score), 100), signals, label: 'Strategic Value', weight: WEIGHTS.strategicValue };
}

// ── Win Posture ─────────────────────────────────────────────────────────────
// Graph signals can shift posture: large contractor ecosystem → partner-first
function deriveWinPosture(components, recommendation, graphSignals) {
  if (recommendation === 'PASS') return 'PASS';

  const overall = Math.round(
    components.capabilityFit.score        * WEIGHTS.capabilityFit +
    components.marketAttractiveness.score * WEIGHTS.marketAttractiveness +
    components.competitivePosture.score   * WEIGHTS.competitivePosture +
    components.executionFeasibility.score * WEIGHTS.executionFeasibility +
    components.strategicValue.score       * WEIGHTS.strategicValue
  );

  const cap  = components.capabilityFit.score;
  const comp = components.competitivePosture.score;
  const gs = graphSignals || {};

  // Dense contractor ecosystem suggests partner-first even when scores qualify for PRIME
  const ecosystemPressure = gs.contractorEcosystemSize >= 8 && gs.connectedContractorCount >= 3;

  if (overall >= 68 && cap >= 55 && comp >= 50 && !ecosystemPressure) return 'PRIME';
  if (overall >= 52 && cap >= 40) return 'PARTNER';
  if (overall >= 35) return 'MONITOR';
  return 'PASS';
}

// ── Model Confidence ────────────────────────────────────────────────────────
// Graph signals (related notices, lifecycle threads) increase confidence
function deriveConfidence(opp) {
  const gs = opp.graphSignals || {};

  // Positive signals (data richness + graph context)
  const positiveSignals =
    ((opp.fitReasons   || []).length   > 0    ? 1 : 0) +
    ((opp.competitiveIntel || '').length > 200 ? 1 : 0) +
    (opp.marketResearch?.intelligence          ? 1 : 0) +
    (opp.aiScrubStatus === 'complete'          ? 1 : 0) +
    ((opp.attachmentText || '').length > 200   ? 1 : 0) +
    (opp.agentPlan                             ? 1 : 0) +
    ((opp.captureTasks || []).length   > 0     ? 1 : 0) +
    (gs.relatedNoticeCount >= 2                ? 1 : 0) +
    (gs.lifecycleThreadCount >= 1              ? 1 : 0);

  // Extraction quality penalty — missing critical fields reduce confidence
  const extractionPenalty =
    (!opp.responseDeadline                     ? 1 : 0) +
    (!opp.agency || opp.agency === 'Unknown'   ? 1 : 0) +
    (!opp.description || opp.description.length < 50 ? 1 : 0) +
    (!opp.naics                                ? 1 : 0);

  const netSignals = positiveSignals - extractionPenalty;

  if (netSignals >= 6) return { label: 'HIGH',   color: 'var(--accent)' };
  if (netSignals >= 3) return { label: 'MEDIUM', color: 'var(--amber)' };
  return                 { label: 'LOW',    color: 'var(--text-2)' };
}

// ── Top-Level Export ────────────────────────────────────────────────────────
export function computeScoringV2(opp) {
  if (!opp || (opp.score == null)) return null;

  const capabilityFit        = computeCapabilityFit(opp);
  const marketAttractiveness = computeMarketAttractiveness(opp);
  const competitivePosture   = computeCompetitivePosture(opp);
  const executionFeasibility = computeExecutionFeasibility(opp);
  const strategicValue       = computeStrategicValue(opp);

  const components = { capabilityFit, marketAttractiveness, competitivePosture, executionFeasibility, strategicValue };

  const overallScore = Math.round(
    capabilityFit.score        * WEIGHTS.capabilityFit +
    marketAttractiveness.score * WEIGHTS.marketAttractiveness +
    competitivePosture.score   * WEIGHTS.competitivePosture +
    executionFeasibility.score * WEIGHTS.executionFeasibility +
    strategicValue.score       * WEIGHTS.strategicValue
  );

  const winPosture = deriveWinPosture(components, opp.recommendation, opp.graphSignals);
  const confidence = deriveConfidence(opp);

  // Pick the strongest single signal from each component, rank by component score
  const topReasons = Object.values(components)
    .filter(c => c.signals.length > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(c => c.signals[0]);

  return { overallScore, winPosture, confidence, topReasons, components };
}

// Posture badge styling — shared between components
export const POSTURE_STYLE = {
  PRIME:   { color: 'var(--accent)', bg: 'var(--accent-dim)',   border: 'var(--accent)' },
  PARTNER: { color: 'var(--blue)',   bg: 'var(--blue-dim)',     border: 'var(--blue)'   },
  MONITOR: { color: 'var(--amber)',  bg: 'var(--amber-dim)',    border: 'var(--amber)'  },
  PASS:    { color: 'var(--red)',    bg: 'var(--red-dim)',      border: 'var(--red)'    },
};
